import { useQuery } from '@apollo/client';
import { Avatar, Badge, Tooltip, withStyles } from '@material-ui/core';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import PauseCircleFilledIcon from '@material-ui/icons/PauseCircleFilled';
import PlayCircleFilledIcon from '@material-ui/icons/PlayCircleFilled';
import { Skeleton } from '@material-ui/lab';
import { Network } from '@mogul/api-client';
import { BigNumber } from 'ethers';
import type { FC, MouseEvent } from 'react';
import { useContext, useEffect, useMemo, useState } from 'react';
import { AmbientLight, DirectionLight, GLTFModel } from 'react-3d-viewer';
import { useHistory } from 'react-router-dom';

import { collectionsApi } from '../../../api';
import { ReactComponent as BSCLogo } from '../../../common/svg/button-icons/bsc-logo.svg';
import { ReactComponent as EthLogo } from '../../../common/svg/button-icons/eth-icon.svg';
import { MarketPlaceContext } from '../../../contexts/app-state-context/marketplace/marketplace.context';
import { Web3StateContext } from '../../../contexts/app-state-context/web3-state/context';
import { useWeb3State } from '../../../hooks';
import { useCloudfrontNftAsset } from '../../../hooks/cloudfront-hook';
import { useContractAddress } from '../../../hooks/contract-address.hooks';
import { useGetNft } from '../../../hooks/nft-api.hook';
import {
  getIsStarsListing,
  getListingEthPrice,
  getListingStarsPrice,
} from '../../../lib/graph/graph-utils';
import type {
  AccountAsset,
  Asset,
  Listing,
} from '../../../lib/graph/MogulMarketplace';
import { ListingType } from '../../../lib/graph/MogulMarketplace';
import accountAssetByIdQuery from '../../../lib/graph/queries/accountAssetByIdQuery';
import { translateIpfsToPinata } from '../../../lib/nfts/nft-catalog.service';
import {
  getNetworkCurrencySymbol,
  weiToEth,
} from '../../../lib/smart-contract-utils';
import { collectionHasUnlockableContent } from '../../../utils/collection.util';
import { MediaDisplay } from '../../core/media-display/MediaDisplay.component';
import styles from './NftCard.module.css';

const CustomTooltip = withStyles(theme => ({
  tooltip: {
    maxWidth: 220,
    fontSize: theme.typography.pxToRem(14),
  },
}))(Tooltip);

interface NftCardProps {
  asset: Asset;
  yourNfts?: boolean;
  showViewCollection?: boolean;
}

export const NftCard: FC<NftCardProps> = ({
  asset,
  yourNfts,
  showViewCollection = true,
}) => {
  const {
    tokenAddress,
    tokenId,
    image,
    animation_url,
    lastSoldPrice,
    activeListings,
  } = asset;

  const contractAddress = useContractAddress();
  const { lastSoldMap, purchasePriceMap } = useContext(MarketPlaceContext);
  const history = useHistory();
  const { walletDetails, isInitialized } = useWeb3State();
  const [nftNetwork, setNftNetwork] = useState<string | null>(null);
  const [playVideo, setPlayVideo] = useState(false);
  const [currentAuction, __] = useState<Listing | undefined>(
    activeListings != null && activeListings.length > 0
      ? activeListings.find(
          l => l.type === ListingType.Listing || l.endTime > Date.now() / 1000,
        )
      : undefined,
  );

  const isStarsListing = useMemo(
    () => !!currentAuction && getIsStarsListing(currentAuction),
    [currentAuction],
  );
  const listingStarsPrice = useMemo(
    () => !!currentAuction && getListingStarsPrice(currentAuction),
    [currentAuction],
  );
  const listingEthPrice = useMemo(
    () => !!currentAuction && getListingEthPrice(currentAuction),
    [currentAuction],
  );

  const dbNft = useGetNft(
    asset.tokenAddress as string,
    asset.tokenId.toString(),
  );

  // BONDED GLUE
  const title = dbNft?.title;

  const getNftSubheading = () => {
    let firstHeader = '',
      secondHeader = '';

    if (currentAuction) {
      if (currentAuction.type === ListingType.Auction) {
        if (currentAuction.startTime < Date.now() / 1000) {
          firstHeader = 'Current bid';
          secondHeader = 'Ending in';
        } else {
          firstHeader = 'Starting in';
        }
      } else {
        firstHeader = 'Purchase price';
      }
    }

    if (lastSoldPrice) {
      if (!firstHeader) {
        firstHeader = 'Last sold for';
      } else if (!secondHeader) {
        secondHeader = 'Last sold for';
      }
    }

    return { firstHeader, secondHeader };
  };

  const getPriceString = () => {
    if (activeListings != null && activeListings.length > 0) {
      const highestBidAmount = currentAuction?.highestBidAmount as string;
      const listingPrice =
        `${tokenAddress}/${tokenId}` in purchasePriceMap
          ? purchasePriceMap[`${tokenAddress}/${tokenId}`].listingPrice
          : // : currentAuction?.isStarsListing === true
          isStarsListing
          ? // ? (currentAuction.starsPrice as string)
            (listingStarsPrice as string)
          : // : (currentAuction?.ethPrice as string);
          currentAuction
          ? (listingEthPrice as string)
          : '0';

      const auctionUnit =
        `${tokenAddress}/${tokenId}` in purchasePriceMap
          ? purchasePriceMap[`${tokenAddress}/${tokenId}`].isStarsListing
            ? 'MOGUL'
            : getNetworkCurrencySymbol()
          : // : currentAuction?.isStarsListing === true
          isStarsListing
          ? 'MOGUL'
          : getNetworkCurrencySymbol();

      const price = weiToEth(
        BigNumber.from(highestBidAmount || listingPrice || '0'),
      );

      return price + ' ' + auctionUnit;
    }
    return '';
  };

  const navigateToNftDetails = () => {
    const path = `/platform/nftDetails/${walletDetails.chain.key}/${
      asset.tokenAddress as string
    }/${tokenId}`;

    history.push(path);
  };

  const [collectionSlug, setCollectionSlug] = useState(
    asset.tokenAddress as string,
  );
  const navigateToCollection = (
    e: MouseEvent<HTMLButtonElement | HTMLDivElement>,
  ) => {
    e.stopPropagation();

    const path = `/platform/collection/${walletDetails.chain.key}/${collectionSlug}`;

    history.push(path);
  };

  const getClaimNotification = () => {
    let numOfAuctionsToClaim = 0;

    const auctions = activeListings?.filter(
      listing => listing.type === ListingType.Auction,
    );

    for (const auction of auctions ?? []) {
      if (auction.endTime < Date.now() / 1000) {
        const currentAddress = walletDetails.address?.toLowerCase() ?? '';
        const winning = auction.highestBidder?.address as string;
        const seller = auction.seller.address as string;

        if ([winning, seller].includes(currentAddress)) {
          numOfAuctionsToClaim += 1;
        }
      }
    }

    return numOfAuctionsToClaim > 0 ? numOfAuctionsToClaim.toString() : null;
  };

  const { firstHeader, secondHeader } = getNftSubheading();
  const priceString = getPriceString();

  const {
    cloudfrontUrl: imagePreviewCloudfront,
    mimeType: imagePreviewMimetype,
  } = useCloudfrontNftAsset(dbNft?.imagePreview);
  const { cloudfrontUrl: animationCloudfront, mimeType: animationMimetype } =
    useCloudfrontNftAsset(dbNft?.animation);

  const imageUrl = imagePreviewCloudfront ?? translateIpfsToPinata(image);
  const animationUrl =
    imagePreviewCloudfront != null
      ? animationCloudfront
      : translateIpfsToPinata(animation_url);

  const claimNotification = getClaimNotification();

  const assetId = asset.id;
  const walletAddress = walletDetails.address?.toLowerCase() ?? '';
  const marketplaceAddress = contractAddress.MARKETPLACE_ADDRESS.toLowerCase();

  const { data: walletData } = useQuery<{
    accountAsset?: AccountAsset;
  }>(accountAssetByIdQuery, {
    variables: { accountAssetId: `${walletAddress}/${assetId}` },
  });

  const { data: marketplaceData } = useQuery<{
    accountAsset?: AccountAsset;
  }>(accountAssetByIdQuery, {
    variables: { accountAssetId: `${marketplaceAddress}/${assetId}` },
  });

  const refilteredListings =
    marketplaceData?.accountAsset?.asset.activeListings?.filter(
      l => l.seller.address === walletDetails.address?.toLowerCase() ?? '',
    ) ?? [];

  const inInventory = walletData?.accountAsset?.quantity;
  const amountOwnedTotal = inInventory;

  const [hasUnlockableContent, setHasUnlockableContent] = useState(false);
  const [collectionName, setCollectionName] = useState('');

  useEffect(() => {
    void (async () => {
      if (tokenAddress) {
        try {
          const collection = await collectionsApi.getCollectionByAddress({
            contractAddress: tokenAddress,
          });
          setCollectionName(collection.data.data.name);
          setNftNetwork(collection.data.data.network);
          setCollectionSlug(collection.data.data.slug);
        } catch (e) {
          setNftNetwork(null);
        }
      }
      if (isInitialized) {
        const unlockableContent = await collectionHasUnlockableContent(
          tokenAddress,
          tokenId.toString(),
        );
        setHasUnlockableContent(unlockableContent);
      }
    })();
  }, [isInitialized]);

  if (!dbNft) {
    return null;
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onKeyPress={navigateToNftDetails}
      onClick={navigateToNftDetails}
      className={`${styles.card} flex flex-col w-60 cursor-pointer z-0 rounded-lg `}
    >
      <Badge badgeContent={claimNotification} color="primary">
        <div className={`${styles.imageBlock} flex relative`}>
          {imageUrl &&
            (animationMimetype === 'model/gltf-binary' ? (
              <div className="relative w-60 h-60">
                <Avatar
                  style={{
                    width: '100%',
                    height: '100%',
                  }}
                  variant="square"
                  onClick={e => {
                    e.stopPropagation();
                  }}
                >
                  <div className="absolute z-100 right-1 top-1">
                    <CustomTooltip
                      title="This is a 3D model. Try to zoom and rotate the model below!"
                      placement="top"
                      arrow
                    >
                      <HelpOutlineIcon color="secondary" fontSize="small" />
                    </CustomTooltip>
                  </div>

                  <GLTFModel
                    width={300}
                    height={300}
                    rotation={{ x: 0, y: -300, z: 0 }}
                    position={{ x: 0, y: 0, z: -200 }}
                    className="rounded-lg object-cover"
                    src={animationUrl}
                  >
                    <AmbientLight color={0xffffff} />
                    <DirectionLight
                      color={0xffffff}
                      position={{ x: 100, y: 200, z: 100 }}
                    />
                    <DirectionLight
                      color={0xff00ff}
                      position={{ x: -100, y: 200, z: -100 }}
                    />
                  </GLTFModel>
                </Avatar>
              </div>
            ) : playVideo &&
              animationUrl &&
              animationMimetype === 'video/mp4' ? (
              <video
                className="rounded-lg object-cover w-60 h-60"
                src={animationUrl}
                autoPlay
                loop
              />
            ) : playVideo &&
              animationUrl &&
              animationMimetype === 'image/gif' ? (
              <MediaDisplay
                className="rounded-lg object-cover w-60 h-60"
                alt=""
                name={title ?? ''}
                url={animationUrl}
                mimeType={animationMimetype}
              />
            ) : playVideo && animationUrl ? (
              <video
                className="rounded-lg object-cover w-60 h-60"
                src={animationUrl}
                autoPlay
                loop
              />
            ) : playVideo &&
              animationUrl &&
              animationMimetype === 'image/gif' ? (
              <MediaDisplay
                className="rounded-lg object-cover w-60 h-60"
                alt=""
                name={title ?? ''}
                url={animationUrl}
                mimeType={animationMimetype}
              />
            ) : imageUrl ? (
              <MediaDisplay
                className="rounded-lg object-cover w-60 h-60"
                alt=""
                name={title ?? ''}
                url={imageUrl}
                mimeType={imagePreviewMimetype}
              />
            ) : (
              <Skeleton variant="rect" height={240} width={240} />
            ))}
          {animationUrl &&
            animationMimetype !== 'model/gltf-binary' &&
            (playVideo ? (
              <PauseCircleFilledIcon
                className="absolute right-1 bottom-1 w-10 rounded-lg hover:bg-gray-700 z-10"
                style={{ backgroundColor: '#C3565C' }}
                fontSize="large"
                onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  setPlayVideo(false);
                }}
              />
            ) : (
              <PlayCircleFilledIcon
                className="absolute right-1 bottom-1 w-10 rounded-lg hover:bg-gray-700 z-10"
                style={{ backgroundColor: '#C3565C' }}
                fontSize="large"
                onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  setPlayVideo(true);
                }}
              />
            ))}
        </div>
      </Badge>

      <div className={styles.hoverControl}>
        <div className={styles.hoverButtons}>
          {showViewCollection ? (
            <div
              role="button"
              aria-hidden
              className={
                styles.viewButton +
                ' w-full font-semibold flex justify-center items-center cursor-pointer'
              }
              onClick={navigateToCollection}
            >
              VIEW COLLECTION
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};
